import ReactDOM from "react-dom/client"; // Import from 'react-dom/client'
import "./index.css";
import App from "./App";
import WebFont from "webfontloader";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { enqueueSnackbar } from "notistack";
import { getUserFacingErrorMessage, handleError, isApiError } from "./utilities/errors";
import { queryClient } from "./queryClient";
import { enqueueUniqueSnackbar } from "./utilities/snackbarUtils";
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import { cogProtocol } from "@geomatico/maplibre-cog-protocol";
import { getEnvCdnHostname } from "./utilities/cdnUtils";
import { logAppVersions } from "./utilities/version";

// CloudFront protects the CDN with signed cookies (set HttpOnly by the API).
// MapLibre's `cog://` and `pmtiles://` handlers fetch byte ranges through the
// global `fetch` with the default `credentials: 'same-origin'`; because the CDN
// is a different origin from the app, those cookies are dropped and CloudFront
// responds 403. Force `credentials: 'include'` for requests targeting the CDN
// host so the signed cookies are sent. (Requires CloudFront CORS to allow
// credentials + an explicit Access-Control-Allow-Origin for the frontend host.)
const installCdnCredentialedFetch = (): void => {
  if (typeof window === "undefined" || (window as any).__cdnCredentialedFetch) return;

  // Read per request: the host switches to the backend's mediaHost once config loads.
  const isCdnHost = (hostname: string): boolean => hostname === getEnvCdnHostname() || /^cdn[-.]/i.test(hostname);

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    let urlString = "";
    if (typeof input === "string") urlString = input;
    else if (input instanceof URL) urlString = input.href;
    else if (input instanceof Request) urlString = input.url;

    let targetsCdn = false;
    try {
      targetsCdn = isCdnHost(new URL(urlString, window.location.href).hostname);
    } catch {
      targetsCdn = false;
    }

    if (targetsCdn && (!init || init.credentials === undefined)) {
      return nativeFetch(input, { ...init, credentials: "include" });
    }
    return nativeFetch(input, init);
  };

  (window as any).__cdnCredentialedFetch = true;
};

const installConsoleNoiseFilter = (): void => {
  if (typeof window === "undefined" || (window as any).__consoleNoiseFilterInstalled) return;

  const params = new URLSearchParams(window.location.search);
  const debugVerboseLogs = params.get("debugVerboseLogs") === "1" || localStorage.getItem("debugVerboseLogs") === "1";
  if (debugVerboseLogs) return;

  const blockedPatterns = [
    "[FULCRUM-DEBUG]",
    "[LEVEL 3]",
    "[FIELD-MAPPING]",
    "[DISPLAY-NAME]",
    "[FINAL-ITEM]",
    "[DESC]",
    "[NAME]",
    "[MediaDrawer]",
    "[useFulcrumPhoto]",
    "[RelationshipView]",
    "[DataItemDetails]",
    "[PhotoCache]",
    "[Data Grid View]",
    "[MediaResourceRouter]",
    "[FulcrumResource]"
  ];

  const shouldSuppress = (args: unknown[]): boolean => {
    const text = args
      .map((a) => {
        if (typeof a === "string") return a;
        if (a instanceof Error) return a.message;
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      })
      .join(" ");

    return blockedPatterns.some((p) => text.includes(p));
  };

  const nativeLog = console.log.bind(console);
  const nativeWarn = console.warn.bind(console);
  const nativeError = console.error.bind(console);

  console.log = (...args: unknown[]) => {
    if (shouldSuppress(args)) return;
    nativeLog(...args);
  };

  console.warn = (...args: unknown[]) => {
    if (shouldSuppress(args)) return;
    nativeWarn(...args);
  };

  console.error = (...args: unknown[]) => {
    if (shouldSuppress(args)) return;
    nativeError(...args);
  };

  (window as any).__consoleNoiseFilterInstalled = true;
};

// A 503 / network failure from the API used to surface as an uncaught promise
// rejection whenever a fire-and-forget request rejected outside of a try/catch
// or a react-query queryFn. Install a global safety net that recognises our
// structured API/network errors, shows a friendly notification instead, and
// marks the rejection as handled. Genuine programming errors are left untouched
// so real bugs are still visible.
// NOTE: the dev server's "Uncaught runtime errors" overlay is suppressed for
// these errors separately, via the `client.overlay.runtimeErrors` filter in
// `config/webpackDevServer.config.js` (the WDS overlay ignores preventDefault()).
const installApiErrorRejectionHandler = (): void => {
  if (typeof window === "undefined" || (window as any).__apiErrorRejectionHandler) return;

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    if (!isApiError(reason)) return; // let real runtime errors surface

    // Mark the rejection as handled so the browser doesn't log it as an
    // "Uncaught (in promise)" error — it is an expected, handled backend failure.
    event.preventDefault();

    const appError = handleError(reason);
    console.warn("[api] Handled unhandled rejection from API request:", appError.message);
    enqueueUniqueSnackbar(enqueueSnackbar, getUserFacingErrorMessage(reason), {
      variant: "error",
      autoHideDuration: 6000
    });
  });

  (window as any).__apiErrorRejectionHandler = true;
};

installCdnCredentialedFetch();
installConsoleNoiseFilter();
installApiErrorRejectionHandler();

// Log frontend + backend build metadata on load (dev/staging only; 404s in prod
// are silently ignored). Kept non-blocking so startup is never delayed.
void logAppVersions();

// Register MapLibre protocols for spatial dataset rendering (once at app init)
const pmtilesProtocol = new Protocol();
maplibregl.addProtocol("pmtiles", pmtilesProtocol.tile);
maplibregl.addProtocol("cog", cogProtocol);
WebFont.load({
  google: {
    families: ["Mukta:200,300,400,500,600,700,800", "sans-serif"]
  }
});

// Use createRoot to create a root instance and call render on it
const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");
const root = ReactDOM.createRoot(rootElement);

// Re-exported for backwards compatibility. New code should import it from
// "./queryClient" directly — importing it from here drags in the app bootstrap.
export { queryClient };
const env = process.env.REACT_APP_NODE_ENV;

root.render(
  <QueryClientProvider client={queryClient}>
    <App />
    {/* {env === "development" && <ReactQueryDevtools initialIsOpen={false} />} */}
  </QueryClientProvider>
);
